import type { SxProps, Theme } from '@mui/material';
import { Tier } from './affinity.types';

export const PICKER_INPUT_SX: SxProps<Theme> = {
  backgroundColor: 'var(--Base-24, #F4F4F4)',
  borderRadius: '12px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    '& fieldset': { border: 'none' },
    '&:hover fieldset': { border: 'none' },
    '&.Mui-focused fieldset': { border: 'none' },
  },
  '& .MuiInputBase-input': {
    fontFamily: 'CircularStd',
    fontSize: 14,
    fontWeight: 500,
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'var(--Base-300, #B0B0B0)',
    fontFamily: 'CircularStd',
    fontSize: 14,
  },
};

// One palette shared by the Affinity Net graph (category-node tiers) and the
// Affinity Bands tab (affinity grades map to the same tiers). The brand tan is
// reserved for the sub-profession root node.
export const TIER_COLORS: Record<string, string> = {
  product: '#E11D48',
  // Warm orange, kept clearly distinct from the cool L1 purple it used to clash
  // with (was #7C3AED, nearly identical to L1's #9333EA).
  profession: '#EA580C',
  sub_profession: '#AB854A',
  L1: '#9333EA',
  L2: '#0891B2',
  L3: '#16A34A',
  none: '#94A3B8',
};

export const PENDING_COLORS = {
  added: '#16A34A',
  removed: '#DC2626',
};

export const tierColor = (tier: Tier, type?: string): string => {
  if (type === 'product') return TIER_COLORS.product;
  if (type === 'profession') return TIER_COLORS.profession;
  if (type === 'sub_profession') return TIER_COLORS.sub_profession;
  return TIER_COLORS[tier ?? 'none'] ?? TIER_COLORS.none;
};

export const nodeRadius = (type: string, tier: Tier): number => {
  if (type === 'product') return 12;
  if (type === 'profession') return 12;
  if (type === 'sub_profession') return 9;
  if (tier === 'L1') return 7;
  if (tier === 'L2') return 6;
  return 5;
};

const CONTROL_HEIGHT = 40;
const CONTROL_RADIUS = '12px';

const HEADER_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 3,
  flexWrap: 'wrap',
};

const EYEBROW_SX = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '1px',
  textTransform: 'uppercase',
  color: 'var(--accent-text)',
};

const TITLE_SX = { fontSize: 22, fontWeight: 700, color: 'var(--text)', mt: 0.25 };

const SUBTITLE_SX = {
  fontSize: 14,
  color: 'var(--text-2)',
  mt: 0.75,
  maxWidth: 640,
  lineHeight: 1.55,
};

const FIELD_LABEL_SX = { fontSize: 12, fontWeight: 600, color: 'var(--text-2)', mb: 0.75 };

const HINT_SX = { fontSize: 12, color: 'var(--muted)', mt: 1, lineHeight: 1.5 };

const PICKER_WRAP_SX = { maxWidth: 360 };

const URL_ROW_SX = { maxWidth: 560 };

const LIVE_BADGE_SX = {
  px: 0.875,
  py: '2px',
  borderRadius: 999,
  bgcolor: 'var(--tint)',
  color: 'var(--accent-text)',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

const REASON_SX = {
  fontSize: 12,
  color: 'var(--text-2)',
  mt: 1,
  pl: 1.25,
  borderLeft: '2px solid var(--border)',
  lineHeight: 1.5,
};

const FRAME_SX = {
  minHeight: 120,
  border: '1px dashed var(--border)',
  borderRadius: CONTROL_RADIUS,
  color: 'var(--muted)',
  fontSize: 14,
  px: 3,
  py: 2.5,
  mt: 2.5,
};

const URL_FIELD_SX = {
  flex: 1,
  '& .MuiOutlinedInput-root': { height: CONTROL_HEIGHT, borderRadius: CONTROL_RADIUS },
};

const SEGMENTED_SX = {
  flexShrink: 0,
  bgcolor: 'var(--surface-2)',
  borderRadius: CONTROL_RADIUS,
  p: '3px',
  gap: '2px',
  '& .MuiToggleButtonGroup-grouped': {
    border: 0,
    borderRadius: '9px !important',
    textTransform: 'none',
    fontFamily: 'CircularStd',
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: 0,
    color: 'var(--text-2)',
    px: 1.75,
    py: 0.625,
    '&:hover': { bgcolor: 'transparent', color: 'var(--text)' },
    '&.Mui-selected': {
      bgcolor: 'var(--surface)',
      color: 'var(--accent-text)',
      fontWeight: 600,
      boxShadow: '0 1px 2px rgba(16, 24, 40, 0.06)',
      '&:hover': { bgcolor: 'var(--surface)' },
    },
  },
};

const MUTED_TEXT_SX: SxProps<Theme> = { fontSize: 12, color: 'var(--muted)' };

const SUBTLE_TEXT_SX: SxProps<Theme> = { fontSize: 12, color: 'var(--text-2)' };

const SECTION_TITLE_SX: SxProps<Theme> = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text)',
  mb: 0.5,
};

const ROW_LABEL_SX: SxProps<Theme> = { fontSize: 13, fontWeight: 600 };

const INLINE_ROW_SX: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minWidth: 0,
};

const CENTERED_MESSAGE_SX: SxProps<Theme> = { textAlign: 'center', px: 2 };

const THUMBNAIL_SX: SxProps<Theme> = {
  width: 76,
  height: 76,
  borderRadius: 1,
  objectFit: 'cover',
  bgcolor: 'var(--surface-2)',
  flexShrink: 0,
};

const CATEGORY_CHIP_SX: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  px: 1,
  py: 0.25,
  borderRadius: 1,
  bgcolor: 'var(--surface-2)',
  fontSize: 12,
};

export const dotSx = (size: number, color: string): SxProps<Theme> => ({
  width: size,
  height: size,
  borderRadius: '50%',
  bgcolor: color,
  flexShrink: 0,
});

export type DemoMode = 'product' | 'category' | 'url';

export const DEFAULT_PRODUCT = { id: 9077, name: 'VersaDry360' };

const FIELD_LABELS: Record<DemoMode, string> = {
  product: 'Product',
  category: 'Category',
  url: 'Product URL',
};

const EMPTY_PROMPTS: Record<DemoMode, string> = {
  product: 'Pick a product to see its best-matching experts.',
  category: 'Pick a category to see which experts it scores highest.',
  url: 'Paste a product URL to score it against every expert.',
};

const URL_ERRORS: Record<string, string> = {
  InvalidUrl: 'Enter a full product URL, starting with http:// or https://.',
  UnreadablePage: 'We could not read a product from that page. Try the product page itself.',
  NoCategoryMatch: 'That product did not map to any TDC category, so it cannot be scored.',
  FetchFailed: 'That page could not be fetched. It may be blocked or temporarily down.',
  ScraperOutOfCredits: 'The page fetcher is out of credits. Let the dev team know.',
  ScraperAuthFailed: 'The page fetcher is misconfigured. Let the dev team know.',
  ScraperConcurrencyLimit: 'The page fetcher is busy. Try again in a minute.',
  RequestTimeout: 'That page took too long to load. Try again, or try another URL.',
  ClassifierUnavailable: 'Category matching is unavailable right now. Try again shortly.',
};

export function messageFor(err: unknown, mode: DemoMode): string {
  const data = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
  const code = data?.error;
  if (code && URL_ERRORS[code]) return URL_ERRORS[code];
  // e.g. the rate limiter's "Try again in N minutes."
  if (data?.message) return data.message;
  if (mode === 'url') return 'Could not score that URL.';
  return 'Failed to load recommended experts.';
}

export function overlapColor(overlap: number, universal: boolean): string {
  if (universal) return TIER_COLORS.L2;
  if (overlap >= 0.99) return '#16A34A';
  if (overlap > 0) return '#F59E0B';
  return '#CBD5E1';
}

export const DEMO_SX = {
  header: HEADER_ROW_SX,
  eyebrow: EYEBROW_SX,
  title: TITLE_SX,
  subtitle: SUBTITLE_SX,
  fieldLabel: FIELD_LABEL_SX,
  hint: HINT_SX,
  pickerWrap: PICKER_WRAP_SX,
  urlRow: URL_ROW_SX,
  urlField: URL_FIELD_SX,
  segmented: SEGMENTED_SX,
  liveBadge: LIVE_BADGE_SX,
  reason: REASON_SX,
  frame: FRAME_SX,
  thumbnail: THUMBNAIL_SX,
  categoryChip: CATEGORY_CHIP_SX,
  inlineRow: INLINE_ROW_SX,
  centeredMessage: CENTERED_MESSAGE_SX,
  mutedText: MUTED_TEXT_SX,
  subtleText: SUBTLE_TEXT_SX,
  sectionTitle: SECTION_TITLE_SX,
  rowLabel: ROW_LABEL_SX,
};

export const DEMO_COPY = {
  fieldLabels: FIELD_LABELS,
  emptyPrompts: EMPTY_PROMPTS,
};
